import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: #0b1220;
  display: flex;
  flex-direction: column;
  color: #fff;
`;

const TopBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  padding-top: max(1.6rem, calc(env(safe-area-inset-top, 0px) + 1rem));
  background: rgba(15, 23, 42, 0.94);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const TopIconBtn = styled.button`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 999px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  background: ${(p) => (p.$primary ? '#10b981' : 'rgba(255,255,255,0.08)')};
  color: ${(p) => (p.$primary ? '#fff' : '#e2e8f0')};
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    background: ${(p) => (p.$primary ? '#059669' : 'rgba(255,255,255,0.14)')};
  }
  &:active { transform: scale(0.95); }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    transform: none;
  }
`;

const UndoRedoGroup = styled.div`
  display: flex;
  gap: 0.35rem;
`;

const UndoRedoBtn = styled.button`
  width: 2.6rem;
  height: 3.2rem;
  border-radius: 0.55rem;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.1rem;
  background: rgba(255,255,255,0.08);
  color: #e2e8f0;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  flex-shrink: 0;

  &:hover {
    background: rgba(255,255,255,0.14);
    transform: scale(1.05);
  }
  &:active { transform: scale(0.95); }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

const UndoRedoLabel = styled.span`
  font-size: 0.55rem;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.02em;
`;

const StageWrap = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 50% 40%, rgba(16, 185, 129, 0.08), transparent 42%),
    #070b14;
  overflow: hidden;
`;

const Stage = styled.div`
  position: relative;
  width: min(100%, calc((100dvh - 11.5rem) * ${(p) => p.$ratio || 4 / 3}));
  max-width: 100%;
  aspect-ratio: ${(p) => p.$ratio || 4 / 3};
  max-height: 100%;
  background: #111827;
  overflow: hidden;
  touch-action: none;
  cursor: ${(p) => (p.$tool === 'crop' ? 'grab' : p.$tool === 'text' ? 'text' : 'crosshair')};
`;

const StageCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const Frame = styled.div`
  position: absolute;
  inset: 8%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 0 9999px rgba(7, 11, 20, 0.55);
  border-radius: 0.45rem;
  pointer-events: none;

  &::before, &::after {
    content: '';
    position: absolute;
    background: rgba(255,255,255,0.28);
  }
  &::before {
    left: 33.333%; top: 0; bottom: 0; width: 1px;
    box-shadow: 33.333% 0 0 rgba(255,255,255,0.28);
  }
  &::after {
    top: 33.333%; left: 0; right: 0; height: 1px;
    box-shadow: 0 33.333% 0 rgba(255,255,255,0.28);
  }
`;

const FrameLabel = styled.span`
  position: absolute;
  left: 50%;
  bottom: calc(8% + 0.4rem);
  transform: translateX(-50%);
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.78);
  font-size: 0.7rem;
  font-weight: 700;
  pointer-events: none;
`;

const TextLayer = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  min-width: 2rem;
  max-width: 80%;
  padding: 0.15rem 0.35rem;
  border: 1px dashed ${(p) => (p.$active ? '#10b981' : 'transparent')};
  border-radius: 0.3rem;
  color: ${(p) => p.$color || '#fff'};
  font-size: ${(p) => p.$size || 28}px;
  font-weight: 800;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
  cursor: move;
  user-select: none;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.15;
`;

const HiddenTextInput = styled.input`
  position: absolute;
  left: -9999px;
`;

const Dock = styled.div`
  flex-shrink: 0;
  background: #111827;
  padding: 0.55rem 0.7rem calc(0.7rem + env(safe-area-inset-bottom));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const Panel = styled.div`
  min-height: 3.4rem;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  overflow-x: auto;
  padding-bottom: 0.45rem;
`;

const Tools = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.35rem;
`;

const ToolBtn = styled.button`
  flex: 1;
  min-height: 3.35rem;
  border: none;
  border-radius: 0.85rem;
  background: ${(p) => (p.$active ? '#10b981' : '#1f2937')};
  color: ${(p) => (p.$active ? '#fff' : '#e2e8f0')};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.1rem;
  font-weight: 700;
  font-size: 0.68rem;
  letter-spacing: 0.01em;

  span {
    font-size: 1.05rem;
    line-height: 1;
  }
`;

const Chip = styled.button`
  flex-shrink: 0;
  min-height: 2.15rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? '#10b981' : 'rgba(255,255,255,0.12)')};
  background: ${(p) => (p.$active ? 'rgba(16,185,129,0.2)' : '#1f2937')};
  color: #fff;
  font-weight: 700;
  font-size: 0.78rem;
  cursor: pointer;
`;

const ColorDot = styled.button`
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 999px;
  border: 2px solid ${(p) => (p.$active ? '#fff' : 'transparent')};
  background: ${(p) => p.$color};
  cursor: pointer;
  flex-shrink: 0;
`;

const Slider = styled.input`
  flex: 1;
  accent-color: #10b981;
  min-width: 7rem;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #94a3b8;
  white-space: nowrap;
`;

const TOOLS = [
  { id: 'crop', icon: '⛶', label: 'Crop' },
  { id: 'paint', icon: '🖌', label: 'Paint' },
  { id: 'text', icon: 'Aa', label: 'Text' },
];

const COLORS = ['#ffffff', '#111827', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];
const CROP_RATIOS = [
  { id: 'orig', value: 0, label: 'Original' },
];
const FRAME_INSET = 0.08;

function getLayout(stageW, stageH, naturalW, naturalH, zoom, offset) {
  const frameW = stageW * (1 - FRAME_INSET * 2);
  const frameH = stageH * (1 - FRAME_INSET * 2);
  const contain = Math.min(frameW / naturalW, frameH / naturalH);
  const displayW = naturalW * contain * zoom;
  const displayH = naturalH * contain * zoom;
  return {
    frameW,
    frameH,
    displayW,
    displayH,
    left: stageW / 2 + offset.x - displayW / 2,
    top: stageH / 2 + offset.y - displayH / 2,
  };
}

function stagePoint(e, stageEl) {
  const rect = stageEl.getBoundingClientRect();
  const touch = e.touches?.[0] || e.changedTouches?.[0];
  const x = (touch ? touch.clientX : e.clientX) - rect.left;
  const y = (touch ? touch.clientY : e.clientY) - rect.top;
  return { x, y, w: rect.width, h: rect.height };
}

/**
 * Full-screen listing photo editor. Same complete() contract as ImageCropModal.
 */
const PhotoEditorModal = ({
  imageSrc,
  fileName = 'photo.jpg',
  aspect = 4 / 3,
  onCancel,
  onComplete,
}) => {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const textInputRef = useRef(null);
  const dragRef = useRef(null);
  const strokeRef = useRef(null);

  const [tool, setTool] = useState('crop');
  const [ratioId, setRatioId] = useState('orig');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [brushColor, setBrushColor] = useState('#ef4444');
  const [brushSize, setBrushSize] = useState(16);
  const [strokes, setStrokes] = useState([]);
  const [texts, setTexts] = useState([]);
  const [activeTextId, setActiveTextId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const saveHistory = useCallback((newStrokes, newTexts) => {
    setHistory((prev) => {
      const trimmed = prev.slice(0, historyIndex + 1);
      return [...trimmed, { strokes: JSON.parse(JSON.stringify(newStrokes)), texts: JSON.parse(JSON.stringify(newTexts)) }];
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex <= 0) return;
    const snap = history[historyIndex - 1];
    setStrokes(JSON.parse(JSON.stringify(snap.strokes)));
    setTexts(JSON.parse(JSON.stringify(snap.texts)));
    setActiveTextId(null);
    setHistoryIndex((prev) => prev - 1);
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const snap = history[historyIndex + 1];
    setStrokes(JSON.parse(JSON.stringify(snap.strokes)));
    setTexts(JSON.parse(JSON.stringify(snap.texts)));
    setActiveTextId(null);
    setHistoryIndex((prev) => prev + 1);
  }, [history, historyIndex]);

  useEffect(() => {
    setTool('crop');
    setRatioId('orig');
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setStrokes([]);
    setTexts([]);
    setActiveTextId(null);
    setHistory([{ strokes: [], texts: [] }]);
    setHistoryIndex(0);
  }, [imageSrc]);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = imageSrc;
  }, [imageSrc]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel, undo, redo]);

  useLayoutEffect(() => {
    const measure = () => {
      if (!stageRef.current) return;
      const rect = stageRef.current.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w > 0 && h > 0) setStageSize({ w, h });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && stageRef.current) ro.observe(stageRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [imageSrc, ratioId]);

  const cropRatio = ratioId === 'orig' && naturalSize.w
    ? naturalSize.w / naturalSize.h
    : (CROP_RATIOS.find((r) => r.id === ratioId)?.value || aspect);

  const layout =
    stageSize.w && naturalSize.w
      ? getLayout(stageSize.w, stageSize.h, naturalSize.w, naturalSize.h, zoom, offset)
      : null;

  const rotateWorkingImage = (delta) => {
    const src = imgRef.current;
    if (!src) return;
    const swap = Math.abs(delta) === 90;
    const w = swap ? src.naturalHeight : src.naturalWidth;
    const h = swap ? src.naturalWidth : src.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.translate(w / 2, h / 2);
    ctx.rotate((delta * Math.PI) / 180);
    ctx.drawImage(src, -src.naturalWidth / 2, -src.naturalHeight / 2);
    const baked = new Image();
    baked.onload = () => {
      imgRef.current = baked;
      setNaturalSize({ w: baked.naturalWidth, h: baked.naturalHeight });
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    baked.src = canvas.toDataURL('image/jpeg', 0.95);
  };

  const drawStage = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !layout || !stageSize.w) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(stageSize.w * dpr);
    canvas.height = Math.round(stageSize.h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, stageSize.w, stageSize.h);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, stageSize.w, stageSize.h);
    ctx.drawImage(img, layout.left, layout.top, layout.displayW, layout.displayH);

    strokes.forEach((stroke) => {
      if (!stroke.points?.length) return;
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = stroke.size;
      ctx.strokeStyle = stroke.color;
      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      stroke.points.forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
      ctx.restore();
    });
  }, [layout, stageSize, strokes]);

  useEffect(() => {
    drawStage();
  }, [drawStage, texts]);

  const beginStroke = (point) => {
    if (tool !== 'paint') return;
    strokeRef.current = {
      kind: 'paint',
      color: brushColor,
      size: brushSize,
      points: [point],
    };
    setStrokes((prev) => [...prev, strokeRef.current]);
  };

  const extendStroke = (point) => {
    if (!strokeRef.current) return;
    strokeRef.current = {
      ...strokeRef.current,
      points: [...strokeRef.current.points, point],
    };
    setStrokes((prev) => {
      const next = [...prev];
      next[next.length - 1] = strokeRef.current;
      return next;
    });
  };

  const onPointerDown = (e) => {
    if (!stageRef.current) return;
    const point = stagePoint(e, stageRef.current);
    if (tool === 'crop') {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragRef.current = {
        mode: 'pan',
        startX: point.x,
        startY: point.y,
        originX: offset.x,
        originY: offset.y,
      };
      return;
    }
    if (tool === 'text') {
      const id = `t-${Date.now()}`;
      const next = {
        id,
        x: point.x,
        y: point.y,
        text: 'Text',
        color: brushColor,
        size: Math.max(22, brushSize + 10),
      };
      setTexts((prev) => {
        const updated = [...prev, next];
        saveHistory(strokes, updated);
        return updated;
      });
      setActiveTextId(id);
      requestAnimationFrame(() => textInputRef.current?.focus());
      return;
    }
    beginStroke(point);
  };

  const onPointerMove = (e) => {
    if (!stageRef.current) return;
    const point = stagePoint(e, stageRef.current);
    if (dragRef.current?.mode === 'pan') {
      setOffset({
        x: dragRef.current.originX + (point.x - dragRef.current.startX),
        y: dragRef.current.originY + (point.y - dragRef.current.startY),
      });
      return;
    }
    if (dragRef.current?.mode === 'text' && dragRef.current.id) {
      const { id, dx, dy } = dragRef.current;
      setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, x: point.x - dx, y: point.y - dy } : t)));
      return;
    }
    if (strokeRef.current) extendStroke(point);
  };

  const onPointerUp = () => {
    if (strokeRef.current && strokeRef.current.points.length > 1) {
      saveHistory(
        [...strokes.slice(0, -1), strokeRef.current],
        texts
      );
    }
    dragRef.current = null;
    strokeRef.current = null;
  };

  const startTextDrag = (e, item) => {
    e.stopPropagation();
    const point = stagePoint(e, stageRef.current);
    setActiveTextId(item.id);
    dragRef.current = {
      mode: 'text',
      id: item.id,
      dx: point.x - item.x,
      dy: point.y - item.y,
    };
    requestAnimationFrame(() => textInputRef.current?.focus());
  };

  const handleConfirm = useCallback(async () => {
    const img = imgRef.current;
    if (!img || !layout || !naturalSize.w) return;
    setBusy(true);
    try {
      const frameLeft = stageSize.w * FRAME_INSET;
      const frameTop = stageSize.h * FRAME_INSET;
      const outW = 1200;
      const outH = Math.round(outW / cropRatio);
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, outW, outH);

      const sx = ((frameLeft - layout.left) / layout.displayW) * naturalSize.w;
      const sy = ((frameTop - layout.top) / layout.displayH) * naturalSize.h;
      const sw = (layout.frameW / layout.displayW) * naturalSize.w;
      const sh = (layout.frameH / layout.displayH) * naturalSize.h;
      const srcX = Math.max(0, sx);
      const srcY = Math.max(0, sy);
      const srcW = Math.min(naturalSize.w - srcX, Math.max(0, sx + sw - srcX));
      const srcH = Math.min(naturalSize.h - srcY, Math.max(0, sy + sh - srcY));
      if (srcW > 0 && srcH > 0) {
        const dstX = ((srcX - sx) / sw) * outW;
        const dstY = ((srcY - sy) / sh) * outH;
        const dstW = (srcW / sw) * outW;
        const dstH = (srcH / sh) * outH;
        ctx.drawImage(img, srcX, srcY, srcW, srcH, dstX, dstY, dstW, dstH);
      }

      const mapX = (x) => ((x - frameLeft) / layout.frameW) * outW;
      const mapY = (y) => ((y - frameTop) / layout.frameH) * outH;
      const mapS = (size) => (size / layout.frameW) * outW;

      strokes.forEach((stroke) => {
        if (!stroke.points?.length) return;
        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = mapS(stroke.size);
        const pts = stroke.points.map((p) => ({ x: mapX(p.x), y: mapY(p.y) }));
        ctx.strokeStyle = stroke.color;
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        pts.forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.stroke();
        ctx.restore();
      });

      texts.forEach((item) => {
        ctx.save();
        ctx.fillStyle = item.color;
        ctx.font = `800 ${Math.max(16, mapS(item.size))}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.45)';
        ctx.shadowBlur = 6;
        ctx.fillText(item.text, mapX(item.x), mapY(item.y));
        ctx.restore();
      });

      const blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
      });
      if (!blob) throw new Error('Could not export photo');
      const safeName = `${String(fileName || 'photo').replace(/\.\w+$/, '')}.jpg`;
      const file = new File([blob], safeName, { type: 'image/jpeg' });
      onComplete({ file, preview: URL.createObjectURL(blob) });
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  }, [cropRatio, fileName, layout, naturalSize, onComplete, stageSize, strokes, texts]);

  const activeText = texts.find((t) => t.id === activeTextId);

  return (
    <Overlay role="dialog" aria-modal="true" aria-label="Edit photo">
      <TopBar>
        <TopIconBtn type="button" onClick={onCancel} disabled={busy} aria-label="Cancel">
          ✕
        </TopIconBtn>
        <UndoRedoGroup>
          <UndoRedoBtn type="button" onClick={undo} disabled={historyIndex <= 0} aria-label="Undo">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10h10a5 5 0 0 1 0 10H12"/><polyline points="7 14 3 10 7 6"/></svg>
            <UndoRedoLabel>Undo</UndoRedoLabel>
          </UndoRedoBtn>
          <UndoRedoBtn type="button" onClick={redo} disabled={historyIndex >= history.length - 1} aria-label="Redo">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10H11a5 5 0 0 0 0 10h1"/><polyline points="17 14 21 10 17 6"/></svg>
            <UndoRedoLabel>Redo</UndoRedoLabel>
          </UndoRedoBtn>
        </UndoRedoGroup>
        <TopIconBtn type="button" $primary onClick={handleConfirm} disabled={busy || !layout} aria-label="Use photo">
          {busy ? '…' : '✓'}
        </TopIconBtn>
      </TopBar>

      <StageWrap>
        <Stage
          ref={stageRef}
          $ratio={cropRatio}
          $tool={tool}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <StageCanvas ref={canvasRef} />
          {tool === 'crop' ? (
            <>
              <Frame />
              <FrameLabel>Item here</FrameLabel>
            </>
          ) : null}
          {texts.map((item) => (
            <TextLayer
              key={item.id}
              $active={item.id === activeTextId}
              $color={item.color}
              $size={item.size}
              style={{ left: item.x, top: item.y }}
              onPointerDown={(e) => startTextDrag(e, item)}
            >
              {item.text}
            </TextLayer>
          ))}
        </Stage>
      </StageWrap>

      <HiddenTextInput
        ref={textInputRef}
        value={activeText?.text || ''}
        onChange={(e) => {
          const value = e.target.value;
          setTexts((prev) => prev.map((t) => (t.id === activeTextId ? { ...t, text: value } : t)));
        }}
      />

      <Dock>
        <Panel>
          {tool === 'crop' && (
            <>
              {CROP_RATIOS.map((row) => (
                <Chip
                  key={row.id}
                  type="button"
                  $active={ratioId === row.id}
                  onClick={() => setRatioId(row.id)}
                >
                  {row.label}
                </Chip>
              ))}
              <Slider
                type="range"
                min="1"
                max="4"
                step="0.01"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
              <Chip type="button" onClick={() => rotateWorkingImage(-90)}>↺</Chip>
              <Chip type="button" onClick={() => rotateWorkingImage(90)}>↻</Chip>
              <Chip
                type="button"
                onClick={() => {
                  setZoom(1);
                  setOffset({ x: 0, y: 0 });
                }}
              >
                Reset
              </Chip>
            </>
          )}
          {tool === 'paint' && (
            <>
              {COLORS.map((color) => (
                <ColorDot
                  key={color}
                  type="button"
                  $color={color}
                  $active={brushColor === color}
                  onClick={() => setBrushColor(color)}
                  aria-label={color}
                />
              ))}
              <Slider
                type="range"
                min="4"
                max="42"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
              />
              <Chip type="button" onClick={() => setStrokes((prev) => prev.filter((s) => s.kind !== 'paint'))}>
                Clear paint
              </Chip>
            </>
          )}
          {tool === 'text' && (
            <>
              <Hint>{activeText ? 'Type to edit · drag to move' : 'Tap the photo to add text'}</Hint>
              {COLORS.map((color) => (
                <ColorDot
                  key={color}
                  type="button"
                  $color={color}
                  $active={brushColor === color}
                  onClick={() => {
                    setBrushColor(color);
                    if (activeTextId) {
                      setTexts((prev) => prev.map((t) => (t.id === activeTextId ? { ...t, color } : t)));
                    }
                  }}
                />
              ))}
              <Chip
                type="button"
                onClick={() => {
                  if (!activeTextId) return;
                  setTexts((prev) => prev.filter((t) => t.id !== activeTextId));
                  setActiveTextId(null);
                }}
              >
                Delete text
              </Chip>
            </>
          )}
        </Panel>
        <Tools>
          {TOOLS.map((item) => (
            <ToolBtn
              key={item.id}
              type="button"
              $active={tool === item.id}
              onClick={() => setTool(item.id)}
            >
              <span>{item.icon}</span>
              {item.label}
            </ToolBtn>
          ))}
        </Tools>
      </Dock>
    </Overlay>
  );
};

export default PhotoEditorModal;
